import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { canShareStudentForm } from '../utils/studentForm';

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-14px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 6px 16px rgba(99, 102, 241, 0.35); }
  50% { box-shadow: 0 6px 22px rgba(168, 85, 247, 0.55); }
`;

const Aside = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e2e8f0; /* slate-200 */
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 4px 0 24px rgba(15, 23, 42, 0.04);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease;
  width: ${(p) => (p.$open ? '256px' : '80px')};
  overflow: hidden;

  /* Mobile: off-canvas drawer that slides over the page */
  ${(p) => p.$mobile && `
    width: min(280px, 85vw);
    transform: translateX(${p.$drawerOpen ? '0' : '-100%'});
    box-shadow: ${p.$drawerOpen ? '8px 0 32px rgba(15, 23, 42, 0.25)' : 'none'};
    visibility: ${p.$drawerOpen ? 'visible' : 'hidden'};
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease, visibility 0s linear ${p.$drawerOpen ? '0s' : '0.3s'};
  `}
`;

const TopBar = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 16px;
`;

const BrandBadge = styled.div`
  height: 36px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.02em;
  animation: ${pulseGlow} 3.2s ease-in-out infinite;
  transition: transform 0.25s ease;
  &:hover { transform: rotate(-6deg) scale(1.08); }
`;

const BrandName = styled.span`
  font-weight: 800;
  font-size: 17px;
  letter-spacing: -0.01em;
  background: linear-gradient(90deg, #4338ca, #9333ea);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Nav = styled.nav`
  flex: 1;
  padding: 16px 12px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const NavButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  background: ${(p) => (p.$active ? '#eef2ff' : 'transparent')};
  color: ${(p) => (p.$active ? '#4338ca' : '#334155')};
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
`;

const NavLink = styled.a`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 12px;
  padding: 10px 12px;
  color: ${(p) => (p.$active ? '#fff' : '#475569')};
  background: ${(p) => (p.$active ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'transparent')};
  box-shadow: ${(p) => (p.$active ? '0 8px 18px rgba(99, 102, 241, 0.32)' : 'none')};
  text-decoration: none;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  white-space: nowrap;
  opacity: 0;
  animation: ${slideIn} 0.45s ease forwards;
  animation-delay: ${(p) => `${(p.$index || 0) * 55 + 80}ms`};
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

  .nav-icon {
    display: flex;
    flex-shrink: 0;
    transition: transform 0.25s ease;
  }

  &:hover {
    background: ${(p) => (p.$active ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : '#eef2ff')};
    color: ${(p) => (p.$active ? '#fff' : '#4338ca')};
    transform: translateX(3px);
  }
  &:hover .nav-icon { transform: scale(1.15) rotate(-4deg); }

  /* Little marker bar on the active item */
  &::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 50%;
    width: 4px;
    height: ${(p) => (p.$active ? '22px' : '0')};
    transform: translateY(-50%);
    border-radius: 0 4px 4px 0;
    background: #6366f1;
    transition: height 0.25s ease;
  }
`;

const ChildWrap = styled.div`
  margin-top: 4px;
  padding-left: 24px;
  display: ${(p) => (p.$open ? 'block' : 'none')};
`;

const Footer = styled.div`
  margin: 12px;
  border-radius: 14px;
  padding: 14px;
  color: #64748b; /* slate-500 */
  font-size: 12px;
  background: linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%);
  border: 1px solid #e0e7ff;
  animation: ${slideIn} 0.5s ease 0.6s both;
`;

const IconBase = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  />
);

const DashboardIcon = () => (
  <IconBase>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="4" rx="1" />
    <rect x="14" y="10" width="7" height="11" rx="1" />
    <rect x="3" y="13" width="7" height="8" rx="1" />
  </IconBase>
);

const InquiryIcon = () => (
  <IconBase>
    <path d="M5 4h14a1 1 0 0 1 1 1v11.5a1 1 0 0 1-1.53.85L12 14.5l-6.47 2.85A1 1 0 0 1 4 16.5V5a1 1 0 0 1 1-1Z" />
  </IconBase>
);

const TargetIcon = () => (
  <IconBase>
    <circle cx="12" cy="12" r="7" />
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v2" />
    <path d="M21 12h-2" />
    <path d="M12 21v-2" />
    <path d="M5 12H3" />
  </IconBase>
);

const CourseIcon = () => (
  <IconBase>
    <path d="M4 6.5 12 4l8 2.5-8 2.5L4 6.5Z" />
    <path d="M4 11l8 2.5 8-2.5" />
    <path d="M4 15.5 12 18l8-2.5" />
  </IconBase>
);

const WorkIcon = () => (
  <IconBase>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </IconBase>
);

const OfferIcon = () => (
  <IconBase>
    <path d="M7 4h10a1 1 0 0 1 1 1v13l-6-3-6 3V5a1 1 0 0 1 1-1Z" />
  </IconBase>
);

const FormLinkIcon = () => (
  <IconBase>
    <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1" />
    <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" />
  </IconBase>
);

const PerformanceIcon = () => (
  <IconBase>
    <path d="M4 19V9" />
    <path d="M10 19V5" />
    <path d="M16 19v-8" />
    <path d="M3 19h18" />
  </IconBase>
);

const ProfileIcon = () => (
  <IconBase>
    <circle cx="12" cy="9" r="3.2" />
    <path d="M6.5 19a5.5 5.5 0 0 1 11 0" />
  </IconBase>
);

export default function SalesSidebar({ isOpen, currentSection, mobile = false, drawerOpen = false, onClose }) {
  const [expanded, setExpanded] = useState([]);
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  const navigation = useMemo(
    () => ([
      { name: 'Dashboard', key: 'dashboard', href: '/sales/dashboard', icon: <DashboardIcon /> },
      { name: 'Inquiry', key: 'inquiry', href: '/sales/inquiry', icon: <InquiryIcon /> },
      { name: 'Target', key: 'target', href: '/sales/target', icon: <TargetIcon /> },
      { name: 'Course info', key: 'course-info', href: '/sales/course-info', icon: <CourseIcon /> },
      { name: 'My work', key: 'my-work', href: '/sales/my-work', icon: <WorkIcon /> },
      { name: 'Offer letter', key: 'offer-letter', href: '/sales/offer-letter', icon: <OfferIcon /> },
      { name: 'Performance', key: 'performance', href: '/sales/performance', icon: <PerformanceIcon /> },
      ...(canShareStudentForm()
        ? [{ name: 'Student form', key: 'student-form', href: '/sales/student-form', icon: <FormLinkIcon /> }]
        : []),
      { name: 'Profile', key: 'profile', href: '/sales/profile', icon: <ProfileIcon /> },
    ]),
    []
  );

  const isActive = (href) => {
    if (!href || href === '#') return false;
    if (href === '/sales/dashboard') return currentPath === href || currentSection === 'dashboard';
    if (href === '/sales/inquiry') return currentPath === href || currentSection === 'inquiry';
    if (href === '/sales/target') return currentPath === href || currentSection === 'target';
    if (href === '/sales/course-info') return currentPath === href || currentSection === 'course-info';
    if (href === '/sales/my-work') return currentPath === href || currentSection === 'my-work';
    if (href === '/sales/offer-letter') return currentPath === href || currentSection === 'offer-letter';
    if (href === '/sales/performance') return currentPath === href || currentSection === 'performance';
    if (href === '/sales/student-form') return currentPath === href || currentSection === 'student-form';
    if (href === '/sales/profile') return currentPath === href || currentSection === 'profile';
    return currentPath.startsWith(href);
  };

  const toggleExpanded = (name) => {
    setExpanded((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  return (
    <Aside $open={isOpen} $mobile={mobile} $drawerOpen={drawerOpen} aria-hidden={mobile && !drawerOpen}>
      <TopBar style={mobile ? { justifyContent: 'space-between' } : undefined}>
        {isOpen ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BrandBadge>SL</BrandBadge>
            <BrandName>Sales</BrandName>
          </div>
        ) : (
          <BrandBadge>SL</BrandBadge>
        )}
        {mobile && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            style={{ border: 'none', background: 'transparent', padding: 8, borderRadius: 12, color: '#475569', cursor: 'pointer' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </TopBar>
      <Nav>
        {navigation.map((item, index) => {
          const itemActive = isActive(item.href);
          return (
            <div key={item.key || item.name} style={{ marginBottom: 6 }}>
              <NavLink href={item.href} $active={itemActive} $index={index} title={isOpen ? undefined : item.name}>
                <span className="nav-icon">{item.icon}</span>
                {isOpen && <span>{item.name}</span>}
              </NavLink>
            </div>
          );
        })}
      </Nav>
      {isOpen && (
        <Footer>
          <div>
            <div style={{ fontWeight: 700, color: '#4338ca' }}>Sales Suite</div>
            <div style={{ marginTop: 4 }}>Version 1.0.0</div>
          </div>
        </Footer>
      )}
    </Aside>
  );
}
